import { ColDef } from "@ag-grid-community/core";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getDistributionBreakdownTableDataRetrieveQueryKey,
  getDistributionUnfundedBreakdownTableDataRetrieveQueryKey,
} from "gen/endpoints/distributions/distributions";
import {
  useAdjustmentSubEventSelectOptionsRetrieveSuspense,
  useFundFamilySubEventAdjustmentUpsert,
} from "gen/endpoints/fund-family-event-admin/fund-family-event-admin";
import { AdjustableFundFamilySubEventTypeEnum } from "gen/model/adjustableFundFamilySubEventTypeEnum";
import { AdjustmentSubEventItemAPIData } from "gen/model/adjustmentSubEventItemAPIData";
import { FundFamilyEventAPIData } from "gen/model/fundFamilyEventAPIData";
import { get } from "lodash-es";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NavConfirmationConfirmDialog } from "shared/components/form/FormNavConfirmationDialog";
import { LoggedInLayout } from "shared/components/layout/LoggedInLayout";
import {
  ConfirmationDialogProps,
  useConfirmationDialog,
} from "shared/components/modal/confirm/ConfirmationDialog";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActions } from "shared/components/table/box/TableBoxActions";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { MaybernTable } from "shared/components/table/maybern/MaybernTable";
import { useMaybernTableBulkEditController } from "shared/components/table/maybern/MaybernTableBulkEditController";
import { useToast } from "shared/components/toast/useToast";
import { ModalCancelButton } from "shared/modal/ModalCancelButton";
import {
  flattenApiErrorData,
  getApiError,
  getApiErrorFromResponse,
} from "shared/utils/ErrorUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export const CapitalActivityAdjustmentsModal: React.FC<
  ConfirmationDialogProps<
    void,
    {
      fundFamilyEvent: FundFamilyEventAPIData;
      subEventTypeBeingAdjusted: AdjustableFundFamilySubEventTypeEnum;
    }
  >
> = ({
  alertProps: { onClose, ...alertProps },
  extraProps: { fundFamilyEvent, subEventTypeBeingAdjusted },
}) => {
  const { t } = useTranslation(["common", "capitalActivity"]);
  const { showToast, showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const {
    mutateAsync: upsertAdjustmentSubEvent,
    error: upsertError,
    variables: upsertVariables,
    reset,
    isSuccess,
  } = useFundFamilySubEventAdjustmentUpsert();

  const {
    data: { transaction_codes, investments, investor_entities, funds },
  } = useAdjustmentSubEventSelectOptionsRetrieveSuspense(
    fundFamilyEvent.fund_family_id,
    fundFamilyEvent.id!,
    {
      sub_event_type_being_adjusted: subEventTypeBeingAdjusted,
    },
  );

  const {
    gridRef,
    datasource,
    onClickClear,
    onCellValueChanged,
    getErrorForCell,
    rowData,
  } = useMaybernTableBulkEditController<AdjustmentSubEventItemAPIData>({
    getSubmittedFieldValue: useCallback(
      (rowIndex, field) =>
        get(upsertVariables, `data.adjustments.${rowIndex}.${field}`),
      [upsertVariables],
    ),
    getFieldError: useCallback(
      (rowIndex, field) =>
        get(
          getApiErrorFromResponse(upsertError)?.data,
          `adjustments.${rowIndex}.${field}`,
        ),
      [upsertError],
    ),
  });

  const isDirty = rowData.length > 0 && !isSuccess;

  const confirmCancel = useConfirmationDialog(NavConfirmationConfirmDialog);

  const columnDefs = useMemo<ColDef<AdjustmentSubEventItemAPIData>[]>(
    () => [
      {
        field: "transaction_code_id",
        headerName: t(
          "capitalActivity:adjustments.table.columns.transaction_code.title",
        ),
        type: ["dropdownEditable"],
        initialWidth: 200,
        cellEditorParams: {
          title: t(
            "capitalActivity:adjustments.table.columns.transaction_code.cellText",
          ),
          options: transaction_codes.map((t) => ({
            label: t.rich_value,
            value: t.value,
          })),
        },
      },
      {
        field: "investment_id",
        headerName: t(
          "capitalActivity:adjustments.table.columns.investment.title",
        ),
        type: ["dropdownEditable"],
        initialWidth: 200,
        cellEditorParams: {
          title: t(
            "capitalActivity:adjustments.table.columns.investment.cellText",
          ),
          options: investments.map((i) => ({
            label: i.rich_value,
            value: i.value,
          })),
        },
      },
      {
        field: "investor_entity_id",
        headerName: t(
          "capitalActivity:adjustments.table.columns.investor_entity.title",
        ),
        type: ["dropdownEditable"],
        initialWidth: 220,
        cellEditorParams: {
          title: t(
            "capitalActivity:adjustments.table.columns.investor_entity.cellText",
          ),
          options: investor_entities.map((i) => ({
            label: i.rich_value,
            value: i.value,
          })),
        },
      },
      {
        field: "fund_id",
        headerName: t("capitalActivity:adjustments.table.columns.fund.title"),
        type: ["dropdownEditable"],
        initialWidth: 200,
        cellEditorParams: {
          title: t("capitalActivity:adjustments.table.columns.fund.cellText"),
          options: funds.map((f) => ({
            label: f.rich_value,
            value: f.value,
          })),
        },
      },
      {
        field: "target_amount",
        headerName: t("capitalActivity:adjustments.targetAmount"),
        type: ["moneyEditable"],
        editable: true,
        initialWidth: 200,
      },
    ],
    [transaction_codes, investments, investor_entities, funds, t],
  );

  const onSubmit = useCallback(async () => {
    try {
      await upsertAdjustmentSubEvent({
        fundFamilyId: fundFamilyEvent.fund_family_id,
        fundFamilyEventId: fundFamilyEvent.id!,
        data: {
          sub_event_type_being_adjusted: subEventTypeBeingAdjusted,
          adjustments: rowData.filter(
            (row) => row !== null,
          ) as AdjustmentSubEventItemAPIData[],
        },
      });
      await invalidateQueryKeys(queryClient, [
        getDistributionBreakdownTableDataRetrieveQueryKey(
          fundFamilyEvent.fund_family_id,
          fundFamilyEvent.id!,
        ),
        getDistributionUnfundedBreakdownTableDataRetrieveQueryKey(
          fundFamilyEvent.fund_family_id,
          fundFamilyEvent.id!,
        ),
      ]);
      showToast({
        variant: "success",
        message: t("capitalActivity:adjustments.success"),
      });
      onClose();
    } catch (e) {
      const rootListError = flattenApiErrorData(getApiError(e)?.data).find(
        (error) => error.fieldPath === "adjustments",
      );
      if (rootListError) {
        showErrorToast(rootListError.error);
      }
    }
  }, [
    queryClient,
    upsertAdjustmentSubEvent,
    fundFamilyEvent,
    subEventTypeBeingAdjusted,

    onClose,
    rowData,
    showErrorToast,
    showToast,
    t,
  ]);

  const onModalClose = useCallback(async () => {
    if (isDirty) {
      const confirmResult = await confirmCancel();
      if (confirmResult === "submit") {
        await onSubmit();
      } else if (confirmResult === "discard") {
        onClose();
      }
    } else {
      onClose();
    }
  }, [confirmCancel, isDirty, onClose, onSubmit]);

  return (
    <Modal
      size="full"
      scrollBehavior="inside"
      onClose={onModalClose}
      {...alertProps}
    >
      <ModalContent>
        <LoggedInLayout />
        <ModalBody px="0" display="flex" flexDirection="column">
          <TableBox isBulkEditor={true}>
            <TableBoxHeader bg="eggplant.25">
              <TableBoxTitle>
                {t("capitalActivity:adjustments.title")}
              </TableBoxTitle>
              <TableBoxActions showFullScreen={false}>
                <Button
                  size="xs"
                  colorScheme="secondary"
                  onClick={() => {
                    onClickClear();
                    reset();
                  }}
                >
                  {t("common:clear")}
                </Button>
              </TableBoxActions>
            </TableBoxHeader>
            <TableBoxBody display="flex" minHeight="0">
              <MaybernTable
                ref={gridRef}
                onCellValueChanged={onCellValueChanged}
                columnDefs={columnDefs}
                defaultColDef={{
                  sortable: false,
                }}
                getErrorForCell={getErrorForCell}
                enableRangeSelection={true}
                enableFillHandle={true}
                rowModelType="serverSide"
                serverSideDatasource={datasource}
                maxConcurrentDatasourceRequests={1}
              />
            </TableBoxBody>
          </TableBox>
        </ModalBody>
        <ModalFooter borderTopWidth="1px" gap="8px" py="12px" px="24px">
          <ModalCancelButton />
          <Button onClick={onSubmit}>
            {t("capitalActivity:adjustments.save")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
